import {signIn, type Session} from "./auth"
import {type BlobItem, listBlobs, StorageError} from "./blobs"
import {
  compareVersionsDescending,
  fileName,
  parseArchivedRelease,
  parsePluginBlob,
  parseReleaseFolder,
  parseTypelibBlob,
} from "./catalog"
import {clientId, repositoryUrl} from "./config"
import {blobSasUrl, getUserDelegationKey, type UserDelegationKey} from "./sas"
import {matchesQuery} from "./search"
import {makeSortable} from "./sortable"

type Child = Node | string | null | undefined

function el(tag: string, attributes: Record<string, string> = {}, ...children: Child[]): HTMLElement {
  const element = document.createElement(tag)
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value)
  for (const child of children) if (child != null) element.append(child)
  return element
}

/** A table row whose search text also covers what is not shown in full, such as whole hashes and blob paths. */
function row(attributes: Record<string, string>, searchExtra: string[], ...cells: HTMLElement[]): HTMLElement {
  const tr = el("tr", attributes, ...cells)
  tr.dataset.search = [tr.textContent ?? "", ...searchExtra].join(" ").toLowerCase()
  return tr
}

function table(headings: string[], rows: HTMLElement[]): HTMLElement {
  if (rows.length === 0) return el("p", {class: "note"}, "Nothing here.")
  const result = el("table", {}, el("thead", {}, el("tr", {}, ...headings.map((h) => el("th", {}, h)))), el("tbody", {}, ...rows))
  makeSortable(result)
  return result
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function hash(sha256: string | undefined): Child {
  return sha256 ? el("code", {title: sha256}, `${sha256.slice(0, 16)}…`) : ""
}

function provenance(metadata: Record<string, string>): Child {
  if (metadata.commit) {
    const commit = el("a", {href: `${repositoryUrl}/commit/${metadata.commit}`}, el("code", {}, metadata.commit.slice(0, 9)))
    return metadata.run ? el("span", {}, commit, " · ", el("a", {href: metadata.run}, "run")) : commit
  }
  return metadata.source ?? ""
}

const productNames: Record<string, string> = {
  "my-schedule": "My Schedule",
  "auto-mps": "Auto MPS",
  "auto-batchprocess": "Auto BatchProcess",
  "asprova": "Asprova",
  "plugin-manager": "Plug-in manager",
}

// ---- Tabs and search ----

// The URL hash names the tab. The panels' ids differ from it (panel-*) so that a hash does not scroll
// the page down to its panel.
const tabs = ["releases", "plugins", "typelibs"] as const
type Tab = (typeof tabs)[number]
let activeTab: Tab = "releases"
const rowNouns: Record<Tab, string> = {releases: "releases", plugins: "builds", typelibs: "files"}

function tabFromHash(): Tab {
  const id = location.hash.slice(1)
  return (tabs as readonly string[]).includes(id) ? (id as Tab) : "releases"
}

function selectTab(tab: Tab, focus = false) {
  activeTab = tab
  for (const id of tabs) {
    const button = document.getElementById(`tab-${id}`)!
    button.setAttribute("aria-selected", String(id === tab))
    button.tabIndex = id === tab ? 0 : -1
    document.getElementById(`panel-${id}`)!.hidden = id !== tab
  }
  if (focus) document.getElementById(`tab-${tab}`)!.focus()
  if (location.hash.slice(1) !== tab) history.replaceState(null, "", `#${tab}`)
  applySearch()
}

function applySearch() {
  const query = (document.getElementById("search") as HTMLInputElement).value
  let shown = 0
  let total = 0
  for (const tr of document.querySelectorAll<HTMLElement>(`#panel-${activeTab} tbody tr`)) {
    const match = matchesQuery(tr.dataset.search ?? "", query)
    tr.hidden = !match
    total++
    if (match) shown++
  }
  document.getElementById("match-count")!.textContent =
    query.trim() === "" ? `${total} ${rowNouns[activeTab]}` : shown === 0 ? "No matches" : `${shown} of ${total}`
}

function setUpTabs() {
  for (const id of tabs) {
    document.getElementById(`tab-${id}`)!.addEventListener("click", () => selectTab(id))
  }
  document.querySelector('[role="tablist"]')!.addEventListener("keydown", (event) => {
    const key = (event as KeyboardEvent).key
    const step = key === "ArrowRight" ? 1 : key === "ArrowLeft" ? -1 : 0
    if (step === 0) return
    event.preventDefault()
    selectTab(tabs[(tabs.indexOf(activeTab) + step + tabs.length) % tabs.length], true)
  })
  document.getElementById("search")!.addEventListener("input", applySearch)
  window.addEventListener("hashchange", () => selectTab(tabFromHash()))
  selectTab(tabFromHash())
}

// ---- Downloads ----

let session: Session
let delegationKey: UserDelegationKey | undefined

async function download(button: HTMLButtonElement, container: string, blobName: string, saveAs: string) {
  button.disabled = true
  try {
    const now = Date.now()
    // A key outlives the links signed with it, so one serves every download for most of an hour.
    if (!delegationKey || Date.parse(delegationKey.signedExpiry) - now < 20 * 60_000) {
      delegationKey = await getUserDelegationKey(await session.getStorageToken(), new Date(now - 5 * 60_000), new Date(now + 60 * 60_000))
    }
    const url = await blobSasUrl(delegationKey, container, blobName, {
      start: new Date(now - 5 * 60_000),
      expiry: new Date(now + 15 * 60_000),
      contentDisposition: `attachment; filename="${saveAs}"`,
    })
    el("a", {href: url}).click()
  } catch (error) {
    alert(describeError(error, "Starting the download failed"))
  } finally {
    button.disabled = false
  }
}

function downloadIcon(): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg"
  const svg = document.createElementNS(ns, "svg")
  svg.setAttribute("viewBox", "0 0 16 16")
  svg.setAttribute("aria-hidden", "true")
  svg.setAttribute("class", "icon")
  const path = document.createElementNS(ns, "path")
  path.setAttribute("d", "M8 1.5v8.25M4.5 6.5 8 10l3.5-3.5M2.5 12.5v1.5h11v-1.5")
  svg.append(path)
  return svg
}

/** A table cell that downloads `blob`; the column sorts by size. Empty when there is no blob. */
function downloadCell(container: string, blob: BlobItem | undefined, saveAs = blob && fileName(blob.name)): HTMLElement {
  if (!blob || !saveAs) return el("td")
  const button = el("button", {class: "download", type: "button", title: `Download ${blob.name}`}, downloadIcon(), saveAs) as HTMLButtonElement
  button.addEventListener("click", () => download(button, container, blob.name, saveAs))
  return el("td", {"data-sort": String(blob.size)}, el("span", {class: "download-cell"}, button, el("span", {class: "note"}, formatSize(blob.size))))
}

function describeError(error: unknown, what: string): string {
  if (error instanceof StorageError && error.status === 403) {
    return error.container
      ? `${what}: your account cannot read the ${error.container} container. Ask for access to the release archive.`
      : `${what}: your account cannot create download links (Storage Blob Delegator on the storage account).`
  }
  return `${what}: ${error instanceof Error ? error.message : String(error)}`
}

// ---- Sections ----

async function inParallel<T, R>(items: T[], limit: number, work: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let next = 0
  await Promise.all(Array.from({length: Math.min(limit, items.length)}, async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await work(items[index])
    }
  }))
  return results
}

async function renderCiReleases(token: string) {
  const {prefixes} = await listBlobs(token, "packages", {delimiter: "/"})
  const folders = prefixes.map((prefix) => ({prefix, release: parseReleaseFolder(prefix)}))
    .filter((f): f is {prefix: string; release: {date: string; tag: string}} => f.release !== null)
  const bundles = await inParallel(folders, 6, (f) => listBlobs(token, "packages", {prefix: `${f.prefix}bundles/`}))
  const rows = folders
    .map((f, i) => ({...f.release, blobs: bundles[i].blobs}))
    .filter((r) => r.blobs.length > 0)
    .sort((a, b) => b.date.localeCompare(a.date) || b.tag.localeCompare(a.tag))
    .map(({date, tag, blobs}) => {
      const find = (name: string) => blobs.find((b) => fileName(b.name) === name)
      const bundle = find("AsprovaWS.zip")
      const test = find("AsprovaWS-test.zip")
      return row({}, blobs.map((b) => b.name),
        el("td", {}, date),
        el("td", {}, el("a", {href: `${repositoryUrl}/releases/tag/${encodeURIComponent(tag)}`}, tag)),
        downloadCell("packages", bundle, `AsprovaWS_${tag}.zip`),
        downloadCell("packages", test, `AsprovaWS-test_${tag}.zip`),
      )
    })
  document.getElementById("ci-releases")!.replaceChildren(table(["Date", "Tag", "Bundle", "Test bundle"], rows))
}

async function renderArchivedReleases(token: string) {
  const {blobs} = await listBlobs(token, "releases")
  const rows = blobs
    .map((blob) => ({blob, release: parseArchivedRelease(blob.name)}))
    .sort((a, b) => (b.release?.date ?? "").localeCompare(a.release?.date ?? "") || b.blob.name.localeCompare(a.blob.name))
    .map(({blob, release}) => row({}, [blob.name],
      el("td", {}, release?.date ?? ""),
      el("td", {}, release?.version ?? ""),
      downloadCell("releases", blob),
    ))
  document.getElementById("archived-releases")!.replaceChildren(table(["Date", "Version", "Bundle"], rows))
}

async function renderPlugins(token: string) {
  const {blobs} = await listBlobs(token, "plugins")
  const files = blobs.map((b) => parsePluginBlob(b)).filter((f) => f !== null)
  const groups = new Map<string, typeof files>()
  for (const file of files) {
    const key = `${file.product}\n${file.version}\n${file.platform}`
    groups.set(key, [...(groups.get(key) ?? []), file])
  }
  const productOrder = Object.keys(productNames)
  const rows = [...groups.values()]
    .sort((a, b) => productOrder.indexOf(a[0].product) - productOrder.indexOf(b[0].product)
      || compareVersionsDescending(a[0].version, b[0].version)
      || a[0].platform.localeCompare(b[0].platform))
    .map((group, i, all) => {
      const {product, version, platform} = group[0]
      const dll = group.find((f) => f.blob.name.endsWith(".dll"))?.blob
      const pdb = group.find((f) => f.blob.name.endsWith(".pdb"))?.blob
      const main = dll ?? group[0].blob
      const startsGroup = i > 0 && (all[i - 1][0].product !== product || all[i - 1][0].version !== version)
      return row(startsGroup ? {class: "group-start"} : {},
        [...group.map((f) => f.blob.name), main.metadata.sha256 ?? "", main.metadata.commit ?? "", product],
        el("td", {}, productNames[product] ?? product),
        el("td", {}, version),
        el("td", {}, platform),
        downloadCell("plugins", dll),
        downloadCell("plugins", pdb),
        el("td", {}, formatDate(main.lastModified)),
        el("td", {}, hash(main.metadata.sha256)),
        el("td", {class: "source"}, provenance(main.metadata)),
      )
    })
  document.getElementById("plugin-files")!.replaceChildren(
    table(["Product", "Version", "Platform", "DLL", "PDB", "Published", "SHA-256 (DLL)", "Source"], rows))
}

async function renderTypelibs(token: string) {
  const {blobs} = await listBlobs(token, "typelibs")
  const rows = blobs.map((b) => parseTypelibBlob(b)).filter((f) => f !== null)
    .sort((a, b) => compareVersionsDescending(a.asprovaVersion, b.asprovaVersion)
      || Number(a.beta) - Number(b.beta)
      || a.library.localeCompare(b.library)
      || b.blob.lastModified.getTime() - a.blob.lastModified.getTime())
    .map((file) => row({}, [file.blob.name, file.blob.metadata.sha256 ?? ""],
      el("td", {}, file.asprovaVersion, file.beta ? " " : null, file.beta ? el("span", {class: "badge"}, "beta") : null),
      el("td", {}, productNames[file.library] ?? file.library),
      downloadCell("typelibs", file.blob),
      el("td", {}, formatDate(file.blob.lastModified)),
      el("td", {}, hash(file.blob.metadata.sha256)),
      el("td", {class: "source"}, provenance(file.blob.metadata)),
    ))
  document.getElementById("typelib-files")!.replaceChildren(
    table(["Asprova", "Library", "File", "Uploaded", "SHA-256", "Source"], rows))
}

async function load(targetId: string, render: () => Promise<void>) {
  document.getElementById(targetId)!.replaceChildren(el("p", {class: "note"}, "Loading…"))
  try {
    await render()
  } catch (error) {
    document.getElementById(targetId)!.replaceChildren(el("p", {class: "error"}, describeError(error, "Loading failed")))
  }
  applySearch()
}

async function main() {
  const status = document.getElementById("status")!
  if (!clientId) {
    status.textContent = "This page has no app registration configured (src/config.ts)."
    return
  }
  try {
    session = await signIn()
  } catch (error) {
    status.className = "error"
    status.textContent = describeError(error, "Signing in failed")
    return
  }
  const signOut = el("button", {class: "link", type: "button"}, "Sign out")
  signOut.addEventListener("click", () => session.signOut())
  document.getElementById("user")!.replaceChildren(el("span", {}, session.account.username), signOut)

  status.remove()
  document.getElementById("archive")!.hidden = false
  setUpTabs()

  const token = await session.getStorageToken()
  await Promise.all([
    load("ci-releases", () => renderCiReleases(token)),
    load("archived-releases", () => renderArchivedReleases(token)),
    load("plugin-files", () => renderPlugins(token)),
    load("typelib-files", () => renderTypelibs(token)),
  ])
}

main()
